import type { ClinicalQualityScore } from '@cartwheel/api/v1'
import { Button } from '@cartwheel/components'

export const DIMENSION_LABELS: Record<string, string> = {
  clinical_content_quality: 'Clinical Content Quality',
  risk_safety_documentation: 'Risk & Safety Documentation',
  diagnostic_clarity: 'Diagnostic Clarity',
  evidence_based_practice: 'Evidence-Based Practice',
  clinical_formulation: 'Clinical Formulation',
  note_type_accuracy: 'Note Type Accuracy',
  session_duration: 'Session Duration',
}

export const RECOMMENDATION_CONFIG: Record<
  string,
  { label: string; color: string; bg: string; border: string }
> = {
  no_action: {
    label: 'No Action Required',
    color: '#15803d',
    bg: '#f0fdf4',
    border: '#bbf7d0',
  },
  written_feedback_coaching: {
    label: 'Written Feedback + Coaching',
    color: '#a16207',
    bg: '#fefce8',
    border: '#fde68a',
  },
  escalate: {
    label: 'Escalate — Formal Quality Review',
    color: '#c2410c',
    bg: '#fff7ed',
    border: '#fed7aa',
  },
}

const VERDICT_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> =
  {
    pass: { label: 'PASS', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
    needs_review: { label: 'NEEDS REVIEW', color: '#a16207', bg: '#fefce8', border: '#fde68a' },
    fail: { label: 'FAIL', color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
  }

function pctColor(pct: number): string {
  if (pct >= 88.89) return 'text-forest-light'
  if (pct >= 66.67) return 'text-blue-600'
  return 'text-orange'
}

function pctBarColor(pct: number): string {
  if (pct >= 88.89) return 'bg-forest-light'
  if (pct >= 66.67) return 'bg-blue-500'
  return 'bg-orange'
}

function scoreChip(rawScore: number | string | null | undefined): {
  label: string
  className: string
} {
  if (rawScore === 'N/A' || rawScore === null || rawScore === undefined) {
    return {
      label: 'N/A',
      className: 'bg-gray-100 text-charcoal-50 border border-gray-stroke',
    }
  }
  const num = Number(rawScore)
  if (num === 3)
    return {
      label: '3 — Meets',
      className: 'bg-forest-light/10 text-forest-light border border-forest-light/30',
    }
  if (num === 2)
    return {
      label: '2 — Partial',
      className: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    }
  return {
    label: '1 — Does Not Meet',
    className: 'bg-orange/10 text-orange border border-orange/30',
  }
}

function ScoreBar({ percentage }: { percentage: number }) {
  return (
    <div className="w-full bg-gray-stroke rounded-full h-1.5">
      <div
        className={`h-1.5 rounded-full transition-all duration-500 ${pctBarColor(percentage)}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  )
}

export function qualityScoreReportPath(scoreId: string): string {
  return `/v2/clinical-quality/scores/${scoreId}`
}

export function ClinicalQualityScoreDisplay({ score }: { score: ClinicalQualityScore }) {
  const pct = Number(score.percentage) || 0
  const rec =
    score.recommendation && RECOMMENDATION_CONFIG[score.recommendation as string]
      ? RECOMMENDATION_CONFIG[score.recommendation as string]
      : null
  const scoredAt = new Date(score.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const judge = score.judge
  const verdictKey = (judge?.verdict ?? '').toString().toLowerCase()
  const verdictCfg = VERDICT_CONFIG[verdictKey] ?? null

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-stroke">
        <div className="text-center shrink-0 w-20">
          <div className={`text-4xl font-bold ${pctColor(pct)}`}>{pct.toFixed(0)}%</div>
          <div className="text-xs text-charcoal-50 mt-0.5 tabular-nums">
            {score.total_score}/{score.scored_dimensions * 3} pts
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="text-sm font-semibold text-charcoal">{score.score_interpretation}</div>
          <ScoreBar percentage={pct} />
          <div className="flex items-center gap-2 flex-wrap">
            {rec && (
              <span
                className="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ background: rec.bg, color: rec.color, border: `1px solid ${rec.border}` }}
              >
                {rec.label}
              </span>
            )}
            {score.confidence != null && (
              <span className="text-xs text-charcoal-50">Confidence {score.confidence}/100</span>
            )}
          </div>
        </div>
        <Button
          variant="text"
          onClick={() => window.open(qualityScoreReportPath(score.id), '_blank')?.focus()}
        >
          <i className="fa-solid fa-arrow-up-right-from-square mr-1.5 text-xs" aria-hidden="true" />
          Open Report
        </Button>
      </div>

      {(score.flags ?? []).length > 0 && (
        <div className="flex items-start gap-2.5 p-3 bg-orange/5 border border-orange/20 rounded-lg text-sm">
          <span className="text-orange font-bold shrink-0 text-base">⚠</span>
          <div>
            <div className="font-semibold text-orange mb-0.5">Reviewer Flags</div>
            <ul className="list-disc ml-5 text-charcoal-75 space-y-0.5">
              {(score.flags as string[]).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="border border-gray-stroke rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-white-dark border-b border-gray-stroke text-sm font-semibold text-charcoal">
          Dimension Scores
        </div>
        <div className="divide-y divide-gray-stroke/50 bg-white">
          {Object.keys(score.scores ?? {})
            .filter((key) => key in DIMENSION_LABELS)
            .map((key) => {
              const raw = score.scores?.[key]
              const reasoning = score.reasoning?.[key] ?? ''
              const chip = scoreChip(raw)
              return (
                <div key={key} className="flex items-start gap-3 py-3 px-4 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-charcoal">{DIMENSION_LABELS[key]}</div>
                    {reasoning && <p className="text-xs text-charcoal-75 mt-1">{reasoning}</p>}
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${chip.className}`}
                  >
                    {chip.label}
                  </span>
                </div>
              )
            })}
        </div>
      </div>

      {judge && (
        <div className="border border-gray-stroke rounded-lg overflow-hidden bg-white-dark">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-stroke">
            <span className="text-sm font-semibold text-charcoal">Judge Evaluation</span>
            {verdictCfg && (
              <span
                className="inline-flex text-xs font-bold px-2.5 py-1 rounded-full"
                style={{
                  background: verdictCfg.bg,
                  color: verdictCfg.color,
                  border: `1px solid ${verdictCfg.border}`,
                }}
              >
                {verdictCfg.label}
              </span>
            )}
          </div>
          <div className="p-4 space-y-2 bg-white">
            <div className="text-sm text-charcoal-75">
              Overall meta-score:{' '}
              <span className="font-semibold text-charcoal">{judge.overall_score ?? '—'}/100</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(judge.scores ?? {}).map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between px-2 py-1 rounded bg-white-dark"
                >
                  <span className="text-charcoal-75 capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-charcoal">{String(v)}/100</span>
                </div>
              ))}
            </div>
            {(judge.flags ?? []).length > 0 && (
              <div className="text-xs text-orange pt-1">
                <span className="font-semibold">Judge flags:</span>{' '}
                {(judge.flags as string[]).join(' · ')}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="text-xs text-charcoal-50 flex items-center justify-between flex-wrap gap-2 pt-1">
        <span>
          Reviewed by {score.scored_by_name ?? 'system'} on {scoredAt}
        </span>
        {score.ai_metadata?.reviewer?.model && (
          <span className="text-charcoal-50/70">
            {score.ai_metadata.reviewer.model} · {score.ai_metadata.reviewer.input_tokens}↑{' '}
            {score.ai_metadata.reviewer.output_tokens}↓ tokens
          </span>
        )}
      </div>
    </div>
  )
}
